/**
 * PersonalizedView Component
 *
 * Handles product personalisation: users can select ink colours, enter text, and preview it on the product.
 * Uses `useMemo` to memoize derived values like available colors, ink mappings, and initial lines.
 * Uses `useEffect` for validation, resetting lines, and handling modal click-outside behavior.
 * Responsive modal sizing for mobile, tablet, and desktop devices.
 * Validates required fields and minimum order quantities to enable/disable the cart button.
 * Displays a clean UI with colour selection pills, personalisation text inputs, and a live preview overlay.
 * Supports dynamic locale-based assets and configurable headings/labels.
 */

import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import CheckIcon from 'public/icons/ag-icons/personalizedChevRight.svg';
import { Product, Variant } from 'shared/types/product';
import useMobileDevice from 'helpers/hooks/useMobileDevice';
import { useTablet } from 'helpers/hooks/useTabletDevice';
import { PreviewContent } from 'shared/types/product/Product';
import CloseIcon from 'public/icons/ag-icons/Close.svg';
import useLocale from 'helpers/hooks/useLocale';
import { PersonalisationBalls } from 'types/datasource';
import { useFormat } from 'helpers/hooks/useFormat';
import { variant } from 'helpers/mocks/mockData';
import { SelectedAttributes } from '../types';
import { discardRepeatedValues } from '../helpers/discardRepeatedValues';

const inkColors = ['Black', 'Red', 'Blue', 'Green'];

// const inkColorMap: Record<string, string> = {
//   Black: '#1E1E1E',
//   Red: '#C82B26',
//   Blue: '#272B5C',
//   Green: '#008C4E',
// };

interface PersonalizedViewProps {
  perviewContent: PreviewContent;
  product: Product;
  setIsCartDisable?: (isCartDisable: boolean) => void;
  minOrderQuantity?: number;
  quantity?: number;
  personalisationBalls?: PersonalisationBalls;
  colorVariant?: Variant | undefined;
  onPersonalisationChange?: (data: {
    lines: { id: number; text: string }[];
    inkColour: string | undefined;
    inkColourHexCode: string | undefined;
  }) => void;
  selectedAttributes: SelectedAttributes;
  setSelectedAttributes: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleColorVariant?: (variantSku: string) => void;
  setHoveredImage?: (img: string) => void | null | undefined;
  changeRoute: (updatedSku: string) => void;
  onChangeVariant: (updatedSku: string) => void;
  setSelectedColor: any;
  selectedColor: any;
  variant?: Variant;
}

const PersonalizedView: FC<PersonalizedViewProps> = ({
  perviewContent,
  product,
  setIsCartDisable,
  minOrderQuantity,
  quantity,
  personalisationBalls,
  colorVariant,
  onPersonalisationChange,
  selectedAttributes,
  setSelectedAttributes,
  handleColorVariant,
  setHoveredImage,
  changeRoute,
  onChangeVariant,
  setSelectedColor,
  selectedColor,
  variant,
}) => {
  const availableColors = useMemo(() => colorVariant?.attributes?.personalisedInkColour ?? [], [colorVariant]);

  const { isMobileDevice } = useMobileDevice();
  const isTablet = useTablet();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Default to first color if available
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const modalWidth =
    (isMobileDevice && perviewContent?.modalWidth?.mobile) ||
    (isTablet && perviewContent?.modalWidth?.tablet) ||
    perviewContent?.modalWidth?.desktop ||
    '560px';
  const modalHeight =
    (isMobileDevice && perviewContent?.modalHeight?.mobile) ||
    (isTablet && perviewContent?.modalHeight?.tablet) ||
    perviewContent?.modalHeight?.desktop ||
    '378px';
  const modalRef = useRef<HTMLDivElement>(null);
  const headingStyle = perviewContent?.headingStyle || '';
  const { getCountry, getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const personalisedCharacterLimit =
    product?.variants?.[0]?.attributes && product?.variants?.[0]?.attributes?.personalisedCharacterLimit;
  const previewImageUrl = useMemo(() => {
    return variant
      ? variant?.attributes?.personalisedPreviewImage
      : colorVariant
      ? colorVariant?.attributes?.personalisedPreviewImage
      : null;
  }, [variant, selectedColor]);

  const isColorSelected = !!selectedColor; // true if a color is selected
  // Ink colors and mapping from personalisationBalls
  const inkColors = useMemo(() => personalisationBalls?.inkColours?.map((c) => c.name), [personalisationBalls]);
  const inkColorsAccordingToColour = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.colour === i?.attributes?.colour);
  }, [selectedAttributes?.colour]);
  const inkColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    // availableColors?.forEach((color) => {
    // Find matching inkColour by name (case-insensitive)
    const match = personalisationBalls?.inkColours?.find(
      (ink: any) => ink?.name?.toLowerCase() === selectedColor?.toLowerCase(),
    );
    (map as any)[selectedColor] = match ? match.hex : '#1E1E1E';
    // });
    return map;
  }, [availableColors, selectedColor, personalisationBalls]);

  // Personalisation lines from personalisationBalls
  const initialLines = useMemo(
    () =>
      personalisationBalls?.personalisationLines?.map((line, idx) => ({
        id: idx + 1,
        text: '',
        required: line.required,
        label: line.text,
      })) || [
        { id: 1, text: '', required: true, label: 'Line 1 - max 15 characters (required)' },
        { id: 2, text: '', required: false, label: 'Line 2 - max 15 characters (optional)' },
        { id: 3, text: '', required: false, label: 'Line 3 - max 15 characters (optional)' },
      ],
    [personalisationBalls],
  );

  const [lines, setLines] = useState(initialLines);

  // Reset lines if personalisationBalls changes
  // useEffect(() => {
  //   setLines(initialLines);
  // }, [initialLines]);
  const handleLineChange = (id: number, value: string) => {
    // Prevent leading space
    // Block first space (but allow spaces after the first character)
    if ((value?.length === 1 || value?.length === 2) && value === ' ') {
      return;
    }
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, text: value } : line)));
  };
  const sortedColourContent = useMemo(() => {
    if (!product?.variants) return [];
    const colourContent = product?.variants || [];
    const sortedColourContent = [...colourContent]?.sort((a, b) => {
      const colourA = a?.attributes?.colour?.toLowerCase() || '';
      const colourB = b?.attributes?.colour?.toLowerCase() || '';
      return colourA?.localeCompare(colourB);
    });
    return discardRepeatedValues(sortedColourContent, 'colour');
  }, [variant]);
  const inKcolours = useMemo(() => {
    const source = inkColorsAccordingToColour?.length > 0 ? inkColorsAccordingToColour : product?.variants;

    return discardRepeatedValues(source, 'personalisedInkColour');
  }, [inkColorsAccordingToColour]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsPreviewOpen(false);
      }
    };

    if (isPreviewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPreviewOpen]);

  useEffect(() => {
    if (!setIsCartDisable) return;

    // Check if any required line is not filled (min 2 non-space chars)
    const hasInvalidRequiredLine = lines?.some(
      (line) => line?.required && (!line?.text || line?.text?.trim()?.length < 2),
    );

    // Disable cart if any condition is not met
    if (
      (quantity && minOrderQuantity && quantity < minOrderQuantity) ||
      !selectedColor ||
      hasInvalidRequiredLine ||
      !selectedAttributes?.colour
    ) {
      setIsCartDisable(true);
    } else {
      setIsCartDisable(false);
    }
    if (onPersonalisationChange) {
      onPersonalisationChange({
        lines,
        inkColour: selectedColor,
        inkColourHexCode: selectedColor && inkColorMap?.[selectedColor],
      });
    }
  }, [quantity, minOrderQuantity, selectedColor, lines, setIsCartDisable]);
  useEffect(() => {
    const filteredVariants = product?.variants?.filter((i: Variant) =>
      !selectedColor
        ? selectedAttributes?.colour === i?.attributes?.colour
        : selectedAttributes?.colour === i?.attributes?.colour &&
          selectedColor === i?.attributes?.personalisedInkColour,
    );
    if (selectedAttributes?.colour && filteredVariants?.length > 0) {
      changeRoute(filteredVariants?.[0]?.sku);
      onChangeVariant(filteredVariants?.[0]?.sku);
    }
  }, [selectedAttributes?.colour, selectedColor]);
  useEffect(() => {
    if (selectedAttributes?.colour && inKcolours?.length > 0) {
      const isColorAvailable = inKcolours?.some((v: Variant) => v?.attributes?.personalisedInkColour === selectedColor);
      if (!isColorAvailable) {
        setSelectedColor('');
      }
    }
  }, [selectedAttributes?.colour, inKcolours, selectedColor]);
  return (
    <>
      <div className="flex flex-col items-start gap-32 self-stretch" data-qa="personalized-options">
        {/*Choose Colour Section */}
        <div className="flex flex-col items-start justify-start">
          <span className="mb-13 mt-4 text-14 font-normal capitalize">
            {formatProductMessage({ id: 'choose.colour', defaultMessage: 'Choose Colour' })}
          </span>
          <div className="ml-5 flex flex-wrap gap-12">
            {sortedColourContent?.map((variant, index) => {
              const img = Array.isArray(variant?.images) && variant.images.length > 0 ? variant.images?.[0] : null;
              if (!img) {
                return null;
              }

              const isActive =
                // (sortedColourContent?.length === 1 && validOptionsOrder?.length === 1) ||
                selectedAttributes?.colour === variant?.attributes?.colour && variant?.attributes?.colour !== '';
              // if (validOptionsOrder?.length === 1 && selectedAttributes?.colour !== variant?.attributes?.colour) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     colour: variant?.attributes?.colour,
              //   }));
              // }
              return (
                <div
                  key={index}
                  tabIndex={0}
                  onClick={() => {
                    handleColorVariant?.(variant?.sku);
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      colour: variant?.attributes?.colour,
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleColorVariant?.(variant?.sku);
                      setSelectedAttributes((prevAttributes) => ({
                        ...prevAttributes,
                        colour: variant?.attributes?.colour,
                      }));
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  className="h-64 w-64 rounded-[2px] border border-[#E2E7E8]"
                  title={variant?.attributes?.colour}
                  onMouseEnter={() => {
                    if (window.innerWidth > 768) {
                      (setHoveredImage as any)(img as string);
                    }
                  }}
                  onMouseLeave={() => {
                    if (window.innerWidth > 768) {
                      (setHoveredImage as any)('');
                    }
                  }}
                >
                  <div className="relative h-full w-full cursor-pointer rounded-md">
                    <div
                      className="h-[95%] w-[95%] bg-cover bg-no-repeat"
                      style={{
                        backgroundImage: `url(${img})`,
                        backgroundPosition: 'center',
                        backgroundSize: `${variant?.attributes?.cropX}px ${variant?.attributes?.cropY}px`,
                      }}
                    ></div>
                    {isActive && <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[#1DCE8F]" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Ink Colours Section */}
        <div className="flex flex-col items-start gap-16 self-stretch" data-qa="ink-colours-section">
          <h3 className="font-body text-14 font-normal leading-[100%] text-[#1E1E1E]" data-qa="ink-colours">
            {personalisationBalls?.inkColoursLabel || 'Ink Colours'}
          </h3>
          <div
            className="flex w-full flex-wrap content-center items-center justify-between gap-y-16"
            data-qa="ink-colors-options"
          >
            <div
              className="flex flex-1 basis-0 flex-wrap content-center items-center gap-x-4 gap-y-4"
              data-qa="colour-pills-wrapper"
            >
              {inKcolours?.map((i: Variant, index: number) => {
                const isSelected =
                  selectedColor === i?.attributes?.personalisedInkColour &&
                  inKcolours.some((v) => v?.attributes?.personalisedInkColour === selectedColor);
                const baseClasses =
                  'flex px-16 py-8 text-14 font-normal font-body leading-[100%] justify-center items-center rounded-full border';
                const selectedClasses = 'bg-[#1DCE8F] border-[#303030]';
                const unselectedClasses = 'bg-[#FFF] border-[#D9D9D9]';

                return (
                  <button
                    key={index}
                    className={`${baseClasses} ${isSelected ? selectedClasses : unselectedClasses}`}
                    onClick={() => setSelectedColor(i?.attributes?.personalisedInkColour)}
                    data-qa={`color-pills-${i?.attributes?.personalisedInkColour?.toLowerCase()}`}
                  >
                    {i?.attributes?.personalisedInkColour}
                  </button>
                );
              })}
            </div>
            {isColorSelected ? (
              <div
                className="flex h-24 w-24 flex-col items-start rounded-full border border-[#D9D9D9] bg-[#61CA94] px-5 py-7"
                data-qa="color-selected-check"
              >
                <CheckIcon />
              </div>
            ) : (
              <div
                className="right:[-30px] h-24 w-24 rounded-full border border-[#D9D9D9] px-7 py-9"
                data-qa="color-unselected-check"
              ></div>
            )}
          </div>
        </div>

        {/* Personalisation Text Section */}
        <div className="flex w-full flex-col content-center items-start gap-16" data-qa="personalization-input">
          <div data-qa="personlization-input-wrapper" className="flex flex-col items-start gap-12 self-stretch">
            <p
              className="text-center font-body text-14 font-normal leading-[100%] text-[#1E1E1E]"
              data-qa="personlization-label"
            >
              {personalisationBalls?.personalisationTextLabel || 'Add Personalisation Text'}
            </p>

            {lines?.map((line) => {
              const text = line?.text;
              const isComplete = text?.trim()?.length >= 2;

              return (
                <div
                  key={line.id}
                  className="flex w-full flex-wrap content-center items-center gap-16"
                  data-qa={`personalization-line-${line?.id}`}
                >
                  <input
                    type="text"
                    placeholder={line?.label}
                    maxLength={personalisedCharacterLimit ?? 15}
                    value={line?.text}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      const regex = /^[A-Za-z0-9 ]*$/;
                      if (!regex.test(value)) return;
                      handleLineChange(line?.id, value);
                    }}
                    data-qa={`personalization-line-${line?.id}-input`}
                    className="flex min-h-[50px] flex-1 basis-0 items-center gap-8 rounded-sm border border-[#D9D9D9] bg-[#FFFFFF] px-12 py-8 font-body text-14 font-normal leading-[100%] text-[#1E1E1E] focus:border-[#1E1E1E]"
                  />
                  {isComplete ? (
                    <div
                      className="flex h-24 w-24 flex-col items-start rounded-full border border-[#D9D9D9] bg-[#61CA94] px-5 py-7"
                      data-qa={`personalization-selected-check-line-${line?.id}`}
                    >
                      <CheckIcon />
                    </div>
                  ) : (
                    <div
                      className="h-24 w-24 rounded-full border border-[#D9D9D9] px-7 py-9"
                      data-qa={`personalization-unselected-check-line-${line?.id}`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            className="flex h-[42px] min-w-[130px] items-center justify-center rounded-sm border border-[#1E1E1E] bg-[#1E1E1E] p-16 text-center font-body text-14 font-semibold leading-[120%] text-[#F3F3F3]"
            onClick={() => setIsPreviewOpen(true)}
            data-qa="preview-design-button"
          >
            {personalisationBalls?.previewDesignLabel || 'Preview Design'}
          </button>
        </div>
      </div>
      {isPreviewOpen && (
        <>
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-50"
            data-qa="preview-modal"
          >
            <div
              className="relative min-w-[320px] rounded-md bg-white p-32 shadow-lg"
              style={{ width: modalWidth, height: modalHeight }}
              ref={modalRef}
              data-qa="preview-container"
            >
              <div
                className="absolute right-0 top-0 flex h-40 w-40 items-center justify-center gap-10"
                onClick={() => setIsPreviewOpen(false)}
                data-qa="close-icon"
              >
                <div className="flex h-24 w-24 shrink-0 items-center justify-center">
                  <CloseIcon strokeWidth={1} className="cursor-pointer text-[#1E1E1E]" />
                </div>
              </div>
              {/* Header */}
              <div className="flex flex-col items-center gap-24 overflow-y-auto" data-qa="preview-content">
                <div className="flex items-center justify-between" data-qa="preview-heading">
                  <p
                    style={{
                      fontSize: isMobileDevice
                        ? `${headingStyle?.fontSizeMobilePx ?? 18}px`
                        : `${headingStyle?.fontSizeDesktopPx ?? 18}px`,
                      fontWeight: headingStyle?.fontWeight ?? '900',
                      color: headingStyle.textColor ?? '#1E1E1E',
                    }}
                    className="font-body leading-[100%]"
                    data-qa="preview-heading-content"
                  >
                    {perviewContent?.heading || 'Preview'}
                  </p>
                </div>
                <div
                  className="relative flex w-full items-center justify-center"
                  style={{ aspectRatio: '1/1' }}
                  data-qa="preview-image-container"
                >
                  {/* Centered personalization lines */}
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center"
                    style={{ textAlign: 'center' }}
                    data-qa="personalization-preview-lines"
                  >
                    <div className="flex flex-col items-center justify-center gap-4">
                      {lines?.map(
                        (line, idx) =>
                          line?.text &&
                          line?.text?.trim()?.length >= 2 && (
                            <span
                              key={line?.id}
                              style={{
                                color: inkColorMap?.[selectedColor],
                                fontWeight: 900,
                                fontSize: '20px',
                                // wordBreak: 'break-word',
                                width: '100%',
                                // textShadow: '0 1px 2px #fff, 0 -1px 2px #fff', // optional: for contrast
                              }}
                              className="leaing-none text-center font-body uppercase"
                              data-qa={`personalization-preview-line-${line?.id}`}
                            >
                              {line?.text}
                            </span>
                          ),
                      )}
                    </div>
                  </div>
                  {previewImageUrl ? (
                    <>
                      <div data-qa="preview-image">
                        <img
                          src={previewImageUrl}
                          alt="Preview"
                          className="aspect-[1/1] w-[100%]"
                          data-qa="preview-image-url"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {perviewContent?.asset?.[selectedLocale] && (
                        <div data-qa="preview-image">
                          <img
                            src={`https://${perviewContent.asset?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${perviewContent.asset['en-GB'].name}`}
                            alt={perviewContent.asset?.[selectedLocale]?.name}
                            className="aspect-[1/1] w-[100%]"
                            data-qa="preview-image-url"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PersonalizedView;
